import React, { useState, useCallback } from 'react';
import { Scissors, AlertTriangle, CheckCircle, X, Loader2 } from 'lucide-react';
import { auth } from '../../shared/api/firebase/config';

const getBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

export default function OrganizadorProvasModal({ fileId, fileName, isOpen, onClose, onSuccess }) {
  const [orderString, setOrderString] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const parseOrder = useCallback((str) => {
    if (!str.trim()) return null;
    const parts = str.split(',').map((s) => s.trim()).filter(Boolean);
    const nums = parts.map((s) => parseInt(s, 10));
    if (nums.some((n) => isNaN(n) || n < 0)) return null;
    if (new Set(nums).size !== nums.length) return null;
    return nums;
  }, []);

  const handleReorder = useCallback(async () => {
    const newOrder = parseOrder(orderString);
    if (!newOrder || !fileId) return;

    setStatus('loading');
    setMessage('');

    try {
      const token = await getToken(true);
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');

      const baseUrl = getBaseUrl();
      const resp = await fetch(`${baseUrl}/drive/pdf/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ file_id: fileId, new_order: newOrder }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(String(data?.detail || data?.error || `Erro ${resp.status}`));
      }

      setStatus('success');
      setMessage(data?.message || 'PDF reordenado com sucesso!');
      if (typeof onSuccess === 'function') onSuccess(data);
    } catch (err) {
      setStatus('error');
      setMessage(String(err?.message || 'Falha ao reordenar o PDF.'));
    }
  }, [fileId, orderString, parseOrder, onSuccess]);

  const handleClose = useCallback(() => {
    setOrderString('');
    setStatus('idle');
    setMessage('');
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const parsed = parseOrder(orderString);
  const isValid = parsed && parsed.length > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border-t-4 border-[#C5A059] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-[#C5A059]" />
            <h3 className="text-lg font-bold text-[#20243B]">Organizador de Provas</h3>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Subtítulo */}
          <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wide mb-1">Reordenar Páginas</p>

          {/* Arquivo */}
          {fileName && (
            <p className="text-xs text-gray-500 mb-3 truncate" title={fileName}>
              Arquivo: <span className="font-medium text-[#20243B]">{fileName}</span>
            </p>
          )}

          {/* Instruções */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Como usar:</strong> Envie o PDF para a IA (Gemini) e peça para analisar a ordem cronológica das páginas.
              A IA retornará uma sequência como <code className="bg-gray-200 px-1 rounded text-[10px]">4, 1, 0, 3, 2</code>.
              Cole essa sequência abaixo.
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              Índices começam em 0. A quantidade de números deve ser igual ao total de páginas do PDF.
            </p>
          </div>

          {/* Input */}
          <input
            type="text"
            placeholder="0, 2, 1, 3..."
            value={orderString}
            onChange={(e) => { setOrderString(e.target.value); if (status !== 'idle') setStatus('idle'); }}
            disabled={status === 'loading' || status === 'success'}
            className="w-full p-3 border border-gray-300 rounded-lg mb-3 text-sm font-mono focus:ring-2 focus:ring-[#C5A059] focus:border-[#C5A059] outline-none disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
          />

          {/* Validação visual */}
          {orderString.trim() && status === 'idle' && (
            <p className={`text-xs mb-3 ${isValid ? 'text-emerald-600' : 'text-red-500'}`}>
              {isValid
                ? `✓ Permutação válida: ${parsed.length} páginas → [${parsed.join(', ')}]`
                : '✗ Formato inválido. Use números separados por vírgula, sem repetição.'}
            </p>
          )}

          {/* Feedback */}
          {status === 'success' && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-700 font-medium">{message}</p>
            </div>
          )}
          {status === 'error' && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
              <p className="text-xs text-red-700 font-medium">{message}</p>
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 font-medium px-4 py-2 text-sm transition-colors"
            >
              {status === 'success' ? 'Fechar' : 'Cancelar'}
            </button>
            {status !== 'success' && (
              <button
                onClick={handleReorder}
                disabled={status === 'loading' || !isValid}
                className="bg-[#20243B] text-white px-5 py-2 rounded-lg font-medium text-sm hover:bg-[#1a1e30] disabled:opacity-50 transition-all flex items-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sincronizando Drive...
                  </>
                ) : (
                  <>
                    <Scissors className="w-4 h-4" />
                    Aplicar Ordem
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
